/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initParent = {
  id: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initAttr = {
  id: '',
  kind: 'IN',
  wallet: '',
  wallet_before: '',
  wallet_after: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function CashierWallet() {
  const mod = 'cashier_wallet';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [parent, setParent] = useState(initParent);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: '#', align: 'center', width: 50 },
    { id: 'tb.kind', label: 'เพิ่ม/ลด', align: 'left' },
    { id: 'tb.wallet', label: 'จำนวนเงิน', align: 'right', width: 120 },
    { id: 'tb.wallet_before', label: 'ก่อนเปลี่ยน', align: 'right', width: 120 },
    { id: 'tb.wallet_after', label: 'หลังเปลี่ยน', align: 'right', width: 120 },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'center' },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.wallet, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);
    setConfirm(false);

    FetchData('PUT', `/${mod}`, app.token, {
      cashier_id: id,
      wallet: attr.wallet,
      kind: attr.kind,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(result.msg, 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderSearch = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();
          view({ ...find, page: 1 });
        }}
      >
        <MDInput
          name='searchform'
          fullWidth
          value={find.search}
          onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
            ),
            autoComplete: 'off',
          }}
        />
      </MDBox>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        <MDButton
          color='success'
          onClick={() => {
            setAttr({ ...initAttr, kind: 'IN' });
            setConfirm(false);
            setModalAdd(true);
          }}
        >
          <Icon>add</Icon> เพิ่มจำนวนเงิน
        </MDButton>
        <MDButton
          color='error'
          onClick={() => {
            setAttr({ ...initAttr, kind: 'OUT' });
            setConfirm(false);
            setModalAdd(true);
          }}
        >
          <Icon>remove</Icon> ลดจำนวนเงิน
        </MDButton>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography variant='h3'>
              Balance : {Func.numberDisplay(parent.wallet)} บาท
            </MDTypography>
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderButton()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell align='left'>{row.kind}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.wallet)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.wallet_before)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.wallet_after)}</TableCell>
                      <TableCell align='center'>{row.emp_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xs' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {attr.kind === 'IN' ? 'เพิ่มจำนวนเงิน' : 'ลดจำนวนเงิน'}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDInput
                  label='จำนวนเงิน'
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setAttr({ ...attr, wallet: e.target.value })}
                  value={attr.wallet}
                  variant='standard'
                  type='number'
                  fullWidth
                  InputProps={{
                    inputProps: {
                      className: 'text-right',
                      autoComplete: 'off',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view(find);
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderAdd()}
      {renderLoading()}
      {notify}
    </div>
  );
}

export default CashierWallet;
